/**
 * AuthSetup - Initializes authentication for all API calls.
 * Sets up axios request interceptor (adds Bearer token) and
 * response interceptor (auto-retries on 401).
 * Does NOT render children until auth is ready.
 */
import { useEffect, useState, useRef, useCallback } from 'react';
import { useAuth } from '@clerk/clerk-react';
import axios from 'axios';
import { setAuthTokenGetter } from '../../services/projectService';
import { setDirectorsAuthTokenGetter } from '../../services/directorsService';

const AuthSetup = ({ children }) => {
  const { getToken, isSignedIn, isLoaded } = useAuth();
  const [authReady, setAuthReady] = useState(false);
  const reqInterceptorRef = useRef(null);
  const resInterceptorRef = useRef(null);
  const getTokenRef = useRef(getToken);

  // Keep ref in sync so interceptor always uses latest getToken
  useEffect(() => {
    getTokenRef.current = getToken;
  }, [getToken]);

  const setupInterceptors = useCallback(() => {
    // Clean up existing interceptors
    if (reqInterceptorRef.current !== null) {
      axios.interceptors.request.eject(reqInterceptorRef.current);
    }
    if (resInterceptorRef.current !== null) {
      axios.interceptors.response.eject(resInterceptorRef.current);
    }

    // Request interceptor: add Bearer token
    reqInterceptorRef.current = axios.interceptors.request.use(
      async (config) => {
        try {
          const token = await getTokenRef.current();
          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        } catch {
          // Token unavailable — request proceeds without auth
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Response interceptor: auto-retry on 401 (expired token) and 5xx (transient errors)
    resInterceptorRef.current = axios.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config;
        if (!originalRequest) return Promise.reject(error);

        // Auto-retry 401 once with fresh token
        if (error.response?.status === 401 && !originalRequest._retried401) {
          originalRequest._retried401 = true;
          try {
            const freshToken = await getTokenRef.current({ skipCache: true });
            if (freshToken) {
              originalRequest.headers.Authorization = `Bearer ${freshToken}`;
              return axios(originalRequest);
            }
          } catch {
            // Fresh token failed
          }
        }

        // Auto-retry 5xx once (520, 502, 503 = transient proxy/server errors)
        const status = error.response?.status || 0;
        if (status >= 500 && !originalRequest._retried5xx) {
          originalRequest._retried5xx = true;
          await new Promise(r => setTimeout(r, 1000));
          return axios(originalRequest);
        }

        return Promise.reject(error);
      }
    );

    // Token getter for projectService (legacy compat)
    setAuthTokenGetter(() => getTokenRef.current());
    // Token getter for directorsService (SSE fetch — can't use axios interceptors)
    setDirectorsAuthTokenGetter(() => getTokenRef.current());
  }, []);

  useEffect(() => {
    // Wait for Clerk to fully load AND user to be signed in
    if (!isLoaded || !isSignedIn) return;

    setupInterceptors();
    setAuthReady(true);

    return () => {
      if (reqInterceptorRef.current !== null) {
        axios.interceptors.request.eject(reqInterceptorRef.current);
        reqInterceptorRef.current = null;
      }
      if (resInterceptorRef.current !== null) {
        axios.interceptors.response.eject(resInterceptorRef.current);
        resInterceptorRef.current = null;
      }
      setAuthTokenGetter(null);
      setDirectorsAuthTokenGetter(null);
    };
  }, [isLoaded, isSignedIn, setupInterceptors]);

  // Don't render children until interceptors are configured
  if (!authReady) return null;

  return children;
};

export default AuthSetup;
